import React, { FC, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  DocumentDto,
  ExportBillingData,
} from "../../../../../../../api-client";
import { EditBillTable } from "../../exports";
import { useDownload } from "../../../../../../../hooks/useDownload";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { CorrectionEditBillTable } from "../../../../CorrectionBilling/Edit/components/CorrectionEditTable";
import ReceiptIcon from "@mui/icons-material/Receipt";
interface BillCardProps {
  bill: ExportBillingData;
  todayString: string;
  selectedRxId: string | null;
  notes: Record<string, string>;
  setNotes: (billId: string, noteValue: string) => void;
  onRowClick: (row: any) => void;
  setSelectedRxId: (id: string) => void;
  handleMenuOpen: (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any,
  ) => void;
  handleTaxierenClick: (row: any) => void;
  onRemoveAll: (billId: string) => void;
  onUploadRxScan?: (
    billId: string,
    file: File,
  ) => Promise<DocumentDto | undefined>;
  isCorrection?: boolean;
}

export const BillCard: FC<BillCardProps> = ({
  bill,
  todayString,
  selectedRxId,
  notes,
  setNotes,
  onRowClick,
  setSelectedRxId,
  handleMenuOpen,
  handleTaxierenClick,
  onRemoveAll,
  onUploadRxScan,
  isCorrection = false,
}) => {
  const { accountInfo, data, billNumber, userInfo, billId } = bill;
  const { rxs, payerDto, totalPrice } = data;
  const totalPriceFormatted = Number(totalPrice)
    .toFixed(2)
    .toString()
    .replace(".", ",");
  const { handleDownloadPdf } = useDownload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [rxScanDoc, setRxScanDoc] = useState<DocumentDto | undefined>(
    bill.docDto,
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      if (onUploadRxScan) {
        const result = await onUploadRxScan(billId, file);
        if (result) {
          setRxScanDoc(result);
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Box className="bill-card-header">
      <Grid container justifyContent="left" alignItems="center">
        <Grid item xs={1}>
          <IconButton
            onClick={() => onRemoveAll(billId)}
            style={{ color: "#ff5722" }}
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
        </Grid>
        <Grid item xs={11}>
          <Box
            className="bill-card-body"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            position="relative"
          >
            <Typography variant="h6" textAlign="center">
              {payerDto?.name}
            </Typography>
            {bill.paperCenterReceipt?.id && (
              <IconButton
                className="fancy-button"
                onClick={() => handleDownloadPdf(bill.paperCenterReceipt.id)}
                title="Papierzentrum-PDF herunterladen"
              >
                <HistoryEduIcon />
              </IconButton>
            )}
            {bill.billPdf?.id && (
              <IconButton
                className="fancy-button"
                onClick={() => handleDownloadPdf(bill.billPdf.id)}
                title="Rechnung-PDF herunterladen"
              >
                <RequestQuoteIcon />
              </IconButton>
            )}
            {rxScanDoc?.id && (
              <IconButton
                className="fancy-button"
                onClick={() => handleDownloadPdf(rxScanDoc.id!)}
                title="Rezept-Scan herunterladen"
              >
                <ReceiptIcon />
              </IconButton>
            )}
            {/* Rx Scan upload — positioned at right edge */}
            <Box
              sx={{
                position: "absolute",
                right: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
              {uploading ? (
                <CircularProgress size={24} sx={{ mx: 1 }} />
              ) : (
                <Tooltip title={rxScanDoc ? "Rezept-Scan ersetzen" : "Rezept-Scan hochladen"} arrow>
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <AttachFileIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ m: 1 }}>
        <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
          {payerDto?.paperReceivingCenter && (
            <Grid item xs={8}>
              <Typography variant="subtitle1" align="center">
                Papierzentrum
              </Typography>
              <Box className="box">
                <Typography variant="subtitle2">
                  {payerDto.paperReceivingCenter.name}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={4}>
            <Typography variant="subtitle1" align="center">
              Mandant
            </Typography>
            <Box className="box">
              <Typography variant="subtitle2">{accountInfo.name}</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1" align="center">
              Bill-ID
            </Typography>
            <Box className="box">
              <Typography variant="subtitle2">{billNumber}</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1" align="center">
              Startdatum
            </Typography>
            <Box className="box">
              <Typography variant="subtitle2">{todayString}</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1" align="center">
              Erstellt von
            </Typography>
            <Box className="box">
              <Typography variant="subtitle2">
                {userInfo.firstName + " " + userInfo.lastName}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1" align="center">
              Gesamtpreis
            </Typography>
            <Box className="box">
              <Typography variant="subtitle2">
                {totalPriceFormatted} €
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {rxs.length > 0 &&
          (isCorrection ? (
            <CorrectionEditBillTable
              rxs={rxs}
              selectedRxId={selectedRxId}
              onRowClick={(row) => {
                onRowClick(row);
                setSelectedRxId(row.id);
              }}
              onMenuOpen={handleMenuOpen}
              onTaxierenClick={handleTaxierenClick}
              previousBillNumber={bill.previousBillNumber}
              previousBillPdf={bill.previousBillPdf?.id}
              previousPaperReceipt={bill.previousPaperCenterReceipt?.id}
            />
          ) : (
            <EditBillTable
              rxs={rxs}
              selectedRxId={selectedRxId}
              onRowClick={(row) => {
                onRowClick(row);
                setSelectedRxId(row.id);
              }}
              onMenuOpen={handleMenuOpen}
              onTaxierenClick={handleTaxierenClick}
              previousBillNumber={bill.previousBillNumber}
              patientBillId={bill.patientRechnung?.id}
              patientBillReminderId={bill.patientMahnung?.id}
            />
          ))}
        {rxs.length === 0 && (
          <Typography variant="h6" align="center">
            Keine Rechnungsrezepte zum Versenden an die {payerDto?.name} bereit.
          </Typography>
        )}

        <Box className="notes-section">
          <TextField
            sx={{ mr: 1, mb: 1 }}
            className="notes-textfield"
            placeholder="Notizen hinzufügen..."
            fullWidth
            value={notes[billId] || ""}
            onChange={(e) => {
              const updatedNote = e.target.value;
              setNotes(billId, updatedNote);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
